/** Per-marketplace item templates. Photos + details differ by shop. */

export const FIELD_META = {
  title: { label: "Title", kind: "text", group: "Item Details", max: 80, required: true, full: true, counter: true },
  description: { label: "Description", kind: "textarea", group: "Item Details", max: 1500, required: true, counter: true },
  condition: {
    label: "Condition",
    kind: "select",
    group: "Item Details",
    options: ["", "New with tags", "New without tags", "Like new", "Good", "Fair"],
  },
  brand: { label: "Brand", kind: "text", group: "Item Details", hint: "Select or write your own" },
  color: { label: "Primary color", kind: "text", group: "Item Details" },
  secondary_color: { label: "Secondary color", kind: "text", group: "Item Details" },
  tags: { label: "Style tags", kind: "text", group: "Item Details", hint: "Up to 3 tags, comma separated" },
  location: { label: "Location (City and State)", kind: "text", group: "Shipping", hint: "Type a city or ZIP code", required: true, full: true },
  hide_from_friends: { label: "Hide from friends", kind: "checkbox", group: "Additional Details (Private)" },
  quantity: { label: "Quantity", kind: "number", group: "Item Details", required: true },
  category: { label: "Category", kind: "text", group: "Category", hint: "e.g. Women > Dresses > Wedding", required: true, full: true },
  size: { label: "Size", kind: "text", group: "Category", required: true },
  original_price: { label: "Original price", kind: "number", group: "Pricing" },
  price: { label: "Listing price", kind: "number", group: "Pricing", required: true },
  shipping_cost: { label: "Shipping cost", kind: "number", group: "Pricing", hint: "What the buyer pays when you ship on your own", required: true },
  delivery_method: { label: "Delivery method", kind: "readonly", group: "Shipping", required: true },
  free_shipping: { label: "Offer free shipping (I'll pay)", kind: "checkbox", group: "Shipping" },
  minimum_price: { label: "Minimum price (Smart Sell)", kind: "number", group: "Pricing" },
  sku: { label: "SKU", kind: "text", group: "Additional Details (Private)" },
  manufacturer: { label: "Manufacturer", kind: "text", group: "Product details", hint: "Who made it. Blank uses your shop name." },
  country_of_origin: { label: "Country of origin", kind: "text", group: "Product details", hint: "IN for India" },
  package_weight: { label: "Package weight", kind: "number", group: "Product details", hint: "Blank sends 500 grams on Amazon.in." },
  package_weight_unit: {
    label: "Package weight unit",
    kind: "select",
    group: "Product details",
    options: ["", "grams", "kilograms", "pounds", "ounces"],
  },
  battery_quantity: { label: "Number of batteries", kind: "number", group: "Safety & compliance", hint: "0 if this item has no batteries. Amazon still needs the quantity." },
  hsn: { label: "HSN code", kind: "text", group: "Product details", hint: "Amazon calls this External Product Information. Silk saree 5007, cotton saree 5208, cotton bed sheet 6302." },
  catalog_id: { label: "Catalog ID", kind: "text", group: "Item Details", full: true },
  cost_price: { label: "Cost price", kind: "number", group: "Additional Details (Private)" },
};

export const GROUP_ORDER = ["Item Details", "Category", "Product details", "Safety & compliance", "Shipping", "Pricing", "Additional Details (Private)"];

const DEFAULT_FIELDS = ["title", "description", "price", "quantity", "brand", "size", "color", "sku"];

export const MARKET_FORMS = {
  shoptosa: {
    note: "Master listing. Marketplace forms inherit this until you override them.",
    fields: [...DEFAULT_FIELDS, "category", "condition"],
  },
  poshmark: {
    note: "Poshmark-only. These details map to Poshmark's create-listing form and do not change other shops.",
    fields: [
      "title", "description", "condition", "brand", "color", "secondary_color", "tags", "quantity",
      "category", "size", "original_price", "price", "minimum_price", "sku", "cost_price",
    ],
  },
  mercari: {
    note: "Mercari-only. Category specifics such as size go in the title or description. The ship-from address comes from your Shoptosa profile.",
    photoMax: 25,
    photoBytes: 15 * 1024 * 1024,
    photoNote: "max 25 photos and 15mb file size limit",
    fields: ["title", "description", "condition", "brand", "quantity", "category", "delivery_method", "shipping_cost", "free_shipping", "price"],
    fieldOverrides: {
      description: { max: 1000 },
      condition: {
        required: true,
        options: ["", "New (New with tags)", "Like new (New without tags)", "Good (Gently used)", "Fair (Used)", "Poor (Major flaws)"],
      },
      brand: {
        kind: "mercari-brand",
        full: true,
        hint: "Type to search Mercari's brand list, or check No Brand/Not Sure.",
      },
      quantity: { required: true, min: 1, max: 1 },
      category: {
        kind: "mercari-category",
        group: "Item Details",
        required: true,
        full: true,
        hint: "Category specifics (e.g. size) are not supported. Please add them to the title or description. It won't affect your listing.",
      },
      delivery_method: {
        value: "Ship on your own",
        hint: "Mercari only supports ship on your own.",
        full: true,
      },
      shipping_cost: { group: "Shipping", money: true, hint: "" },
      free_shipping: { full: true },
      price: {
        label: "Item price",
        money: true,
        min: 1,
        max: 2000,
        hint: "min. $1.00 / max. $2,000.00",
        priceSummary: true,
      },
    },
  },
  grailed: {
    note: "Grailed lists in the background. It picks department, category, designer, size, style, and condition, uploads the photos, and clicks Publish. A saree is Womenswear > Dresses > Maxi Dresses. Titles are kept to 60 characters, and the price is rounded to a whole dollar. A Banarasi saree uses India as the country of origin. Style is Basics unless the item names another Grailed style. Empty, No Brand, Other, and Independent become Grailed's Designer not listed. A brand Grailed does not list does the same. Add a return address on Grailed once if Select address is empty. If Grailed asks you to verify your phone, that window comes forward. Enter the code Grailed sends, then click List again. If Grailed sends you to set up payment info, that window comes forward too. Add your payout details once, then click List again.",
    fields: ["title", "description", "brand", "category", "size", "color", "condition", "price"],
  },
  facebook: {
    note: "Facebook Marketplace-only. These details map to Marketplace's create-item form and do not change other shops.",
    photoMax: 25,
    photoBytes: 15 * 1024 * 1024,
    photoNote: "Up to 25 photos, 15mb each.",
    fields: ["title", "description", "quantity", "category", "condition", "price", "location", "delivery_method", "tags", "sku", "hide_from_friends"],
    fieldOverrides: {
      title: { max: 99 },
      description: { max: 5000 },
      category: {
        kind: "select",
        label: "Category",
        required: true,
        hint: "Vendoo’s Clothing, Shoes & Accessories maps to Women’s or Men’s clothing & shoes on Facebook.",
        options: [
          "",
          "Women's clothing & shoes",
          "Men's clothing & shoes",
          "Antiques & Collectibles",
          "Arts & Crafts",
          "Auto Parts & Accessories",
          "Baby Products",
          "Books, Movies & Music",
          "Cell Phones & Accessories",
          "Clothing, Shoes & Accessories",
          "Electronics",
          "Furniture",
          "Health & Beauty",
          "Home & Kitchen",
          "Jewelry & Watches",
          "Miscellaneous",
          "Musical Instruments",
          "Office Supplies",
          "Patio & Garden",
          "Pet Supplies",
          "Sporting Goods",
          "Tools & Home Improvement",
          "Toys & Games",
          "Travel & Luggage",
          "Video Games & Consoles",
        ],
      },
      condition: {
        kind: "select",
        required: true,
        options: ["", "New", "Used - Like New", "Used - Good", "Used - Fair"],
      },
      price: { label: "Listing price" },
      delivery_method: {
        kind: "select",
        required: true,
        options: ["", "Shipping", "Local pickup"],
      },
      tags: { label: "Tags", group: "Additional Details (Private)", hint: "Up to 20 tags, comma separated" },
      sku: { hint: "Only visible to you" },
    },
  },
  olx: {
    note: "OLX-only.",
    fields: ["title", "description", "price", "category"],
  },
  ebay: {
    note: "eBay listing copy. Post still uses eBay’s official API.",
    fields: ["title", "description", "price", "quantity", "brand", "size", "color", "sku", "condition", "category"],
  },
  etsy: {
    note: "Etsy listing copy. Post still uses Etsy’s official API.",
    fields: ["title", "description", "price", "quantity", "tags", "sku"],
  },
  depop: {
    note: "Depop-only. Category and brand match Depop's create form. List fills Depop in a background window the same way as Poshmark and Mercari: photos, category, brand, condition, size, colour, package, and the ship-from address from your Shoptosa profile (street, city, state, ZIP, and mobile). A saree lands on Women > Dresses > Other — Depop has no saree leaf. Leave brand empty for No Brand. First listing shows Continue; later listings show List.",
    fields: ["title", "description", "condition", "brand", "size", "color", "category", "price"],
    fieldOverrides: {
      description: { max: 1000 },
      condition: {
        required: true,
        options: ["", "Brand new", "Like new", "Used - Excellent", "Used - Good", "Used - Fair"],
      },
      brand: {
        kind: "depop-brand",
        full: true,
        hint: "Type a brand from Depop's list (Independent is on Depop). Check No Brand if there isn't one.",
      },
      category: {
        kind: "depop-category",
        group: "Item Details",
        required: true,
        full: true,
        hint: "Same Category combobox as Depop. A saree is Women > Dresses > Other.",
      },
      size: {
        hint: "Depop shows size after category. Use One size when the item has no size.",
      },
      price: { label: "Item price", money: true, min: 1 },
    },
  },
  amazon: {
    note: "Same details Seller Central asks for on a new page: manufacturer, country of origin, package weight, and number of batteries. Leave weight blank to send 1 pound, and batteries blank to send 0. Post creates the page and Amazon assigns the ASIN. Paste an ASIN only to sell a page that already exists.",
    fields: [...DEFAULT_FIELDS, "manufacturer", "country_of_origin", "package_weight", "package_weight_unit", "battery_quantity"],
    fieldOverrides: {
      package_weight: { hint: "Blank sends 1 pound." },
      package_weight_unit: { hint: "Pounds for Amazon.com." },
    },
  },
  amazon_in: {
    note: "Coming soon.",
    fields: [...DEFAULT_FIELDS, "manufacturer", "country_of_origin", "package_weight", "package_weight_unit", "battery_quantity", "hsn"],
    fieldOverrides: {
      package_weight: { hint: "Grams. Blank sends 500." },
      package_weight_unit: { hint: "Grams for Amazon.in." },
      country_of_origin: { hint: "IN" },
    },
  },
  walmart: {
    note: "Shopify listing copy.",
    fields: [...DEFAULT_FIELDS],
  },
  flipkart: {
    note: "Click Connect, then Post. A SKU already on Flipkart syncs price and stock. A new product is filled in Seller Hub by the helper and waits for Flipkart to assign the FSN.",
    fields: [...DEFAULT_FIELDS],
  },
  meesho: {
    note: "Meesho-only. Fill uses this pack on their seller form.",
    fields: [...DEFAULT_FIELDS, "category"],
  },
  myntra: { note: "Myntra-only.", fields: [...DEFAULT_FIELDS, "category"] },
  ajio: { note: "AJIO-only.", fields: [...DEFAULT_FIELDS, "category"] },
  nykaa: { note: "Nykaa-only.", fields: [...DEFAULT_FIELDS, "category"] },
  tatacliq: { note: "Tata CLiQ-only.", fields: [...DEFAULT_FIELDS, "category"] },
  pinterest: { note: "Connect Pinterest, then Post sends this item to the catalog.", fields: [...DEFAULT_FIELDS] },
  aliexpress: {
    note: "Connect AliExpress, then Post. Catalog ID is the AliExpress category ID.",
    fields: [...DEFAULT_FIELDS, "catalog_id"],
    fieldOverrides: { catalog_id: { label: "AliExpress category ID", required: true } },
  },
  woocommerce: { note: "Enter the store URL, approve Shoptosa, then Post creates the product.", fields: [...DEFAULT_FIELDS] },
  wix: { note: "Click Connect and approve Shoptosa on your Wix site. Post sends the title, price, and photo.", fields: [...DEFAULT_FIELDS] },
  bonanza: {
    note: "Click Connect and approve your booth, then Post. Catalog ID is the Bonanza category ID.",
    fields: [...DEFAULT_FIELDS, "catalog_id"],
    fieldOverrides: { catalog_id: { label: "Bonanza category ID", required: true } },
  },
  backmarket: { note: "Connect Back Market, then Post. The SKU must already match a Back Market product.", fields: [...DEFAULT_FIELDS] },
  stockx: {
    note: "Click Connect and allow StockX, then Post. Catalog ID is the StockX variant ID.",
    fields: [...DEFAULT_FIELDS, "catalog_id"],
    fieldOverrides: { catalog_id: { label: "StockX variant ID", required: true } },
  },
  goat: {
    note: "Connect Alias, then Post. Catalog ID is the Alias catalog item. Size is the US size.",
    fields: [...DEFAULT_FIELDS, "catalog_id"],
    fieldOverrides: {
      catalog_id: { label: "Alias catalog ID", required: true },
      size: { label: "US size", required: true },
    },
  },
  discogs: {
    note: "Click Connect and allow Discogs, then Post. Catalog ID is the numeric Discogs release ID.",
    fields: [...DEFAULT_FIELDS, "catalog_id"],
    fieldOverrides: { catalog_id: { label: "Discogs release ID", required: true } },
  },
  abebooks: { note: "Connect AbeBooks, then Post adds the book. Brand is used as the author.", fields: [...DEFAULT_FIELDS] },
  squarespace: { note: "Click Connect and pick your site, then Post creates the product.", fields: [...DEFAULT_FIELDS] },
  square: { note: "Click Connect and approve Square, then Post adds the catalog item.", fields: [...DEFAULT_FIELDS] },
  google_shopping: { note: "Coming soon.", fields: [...DEFAULT_FIELDS] },
};

export function suggestHsn(title, category) {
  const text = `${title || ""} ${category || ""}`.toLowerCase();
  if (/bed\s?sheet|bedsheet|bed linen/.test(text)) return { code: "6302", label: "cotton bed sheet" };
  if (/saree|sari/.test(text)) {
    if (text.includes("cotton") && !text.includes("silk")) return { code: "5208", label: "cotton saree" };
    if (/georgette|chiffon|polyester|synthetic/.test(text) && !text.includes("silk")) return { code: "5407", label: "synthetic saree" };
    return { code: "5007", label: "silk saree" };
  }
  return null;
}

export function marketForm(channel) {
  return MARKET_FORMS[channel] || {
    note: "Overrides apply only to this marketplace.",
    fields: DEFAULT_FIELDS,
  };
}

export function fieldMeta(channel, key) {
  const base = FIELD_META[key];
  if (!base) return null;
  const over = (marketForm(channel).fieldOverrides || {})[key] || {};
  return { ...base, ...over };
}

export function emptyMarketForm() {
  return {
    title: "",
    description: "",
    price: "",
    original_price: "",
    minimum_price: "",
    cost_price: "",
    shipping_cost: "",
    free_shipping: false,
    quantity: 1,
    brand: "",
    size: "",
    color: "",
    secondary_color: "",
    sku: "",
    category: "",
    condition: "",
    tags: "",
    location: "",
    delivery_method: "Shipping",
    hide_from_friends: false,
    manufacturer: "",
    country_of_origin: "",
    package_weight: "",
    package_weight_unit: "",
    battery_quantity: "",
    hsn: "",
    catalog_id: "",
    images: [],
  };
}

export function formFromPack(pack, fallback) {
  const src = pack || fallback || {};
  const tags = Array.isArray(src.tags) ? src.tags.join(", ") : (src.tags || "");
  return {
    ...emptyMarketForm(),
    title: src.title || "",
    description: src.description || "",
    price: src.price ?? "",
    original_price: src.original_price ?? "",
    minimum_price: src.minimum_price ?? "",
    cost_price: src.cost_price ?? "",
    shipping_cost: src.shipping_cost ?? "",
    free_shipping: src.shipping_cost === 0 || src.shipping_cost === "0",
    quantity: src.quantity ?? 1,
    brand: src.brand || "",
    size: src.size || "",
    color: src.color || "",
    secondary_color: src.secondary_color || "",
    sku: src.sku || "",
    category: src.category || "",
    condition: src.condition || "",
    tags,
    location: src.location || "",
    delivery_method: src.delivery_method || "Shipping",
    hide_from_friends: !!src.hide_from_friends,
    manufacturer: src.manufacturer || "",
    country_of_origin: src.country_of_origin || "",
    package_weight: src.package_weight ?? "",
    package_weight_unit: src.package_weight_unit || "",
    battery_quantity: src.battery_quantity ?? "",
    hsn: src.hsn || "",
    catalog_id: src.catalog_id || "",
    images: Array.isArray(src.images) ? [...src.images] : [],
  };
}
